import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import {
    Plus,
    Trash2,
    Pencil,
    Upload,
    LogOut,
    Mail,
    BarChart3,
    Briefcase,
    X,
    Loader2,
    FileText,
} from "lucide-react";

const emptyProject = {
    slug: "",
    title: "",
    project_type: "",
    short_description: "",
    long_description: "",
    services_used: [],
    cover_image: "",
    gallery: [],
    client_name: "",
    location: "",
    year: "",
    published: true,
    sort_order: 0,
};

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [tab, setTab] = useState("projects");
    const [projects, setProjects] = useState([]);
    const [inquiries, setInquiries] = useState([]);
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // project being edited or null
    const [activeInq, setActiveInq] = useState(null);

    const token = localStorage.getItem("admin_token");

    const logout = () => {
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_email");
        navigate("/admin/login");
    };

    const loadAll = async () => {
        setLoading(true);
        try {
            const [p, i, a] = await Promise.all([
                api.get("/admin/portfolio"),
                api.get("/admin/inquiries"),
                api.get("/admin/analytics/summary"),
            ]);
            setProjects(p.data);
            setInquiries(i.data);
            setAnalytics(a.data);
        } catch (err) {
            if (err?.response?.status === 401) {
                logout();
            } else {
                toast.error("Could not load dashboard");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!token) return;
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!token) return <Navigate to="/admin/login" replace />;


    const tabs = [
        { id: "projects", label: "Projects", icon: Briefcase, count: projects.length },
        { id: "inquiries", label: "Enquiries", icon: Mail, count: inquiries.length },
        { id: "analytics", label: "Analytics", icon: BarChart3 },
    ];

    return (
        <div className="min-h-screen bg-background">
            <header className="border-b border-border sticky top-0 bg-background/90 backdrop-blur-xl z-30">
                <div className="container-edge flex items-center justify-between py-4">
                    <div className="flex items-center gap-3">
                        <span className="font-display text-lg font-medium tracking-tight">
                            Alfie Carter — Admin
                        </span>
                        <span className="overline">{localStorage.getItem("admin_email")}</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <a
                            href="/"
                            className="text-[12px] tracking-[0.15em] uppercase text-foreground/60 hover:text-foreground"
                        >
                            View site →
                        </a>
                        <button
                            data-testid="admin-logout"
                            onClick={logout}
                            className="text-[12px] tracking-[0.15em] uppercase border border-border px-3 py-2 hover:bg-foreground hover:text-background transition-colors inline-flex items-center gap-2"
                        >
                            <LogOut size={14} /> Logout
                        </button>
                    </div>
                </div>
                <div className="container-edge flex gap-2 -mb-px">
                    {tabs.map((t) => {
                        const Icon = t.icon;
                        const active = tab === t.id;
                        return (
                            <button
                                key={t.id}
                                data-testid={`admin-tab-${t.id}`}
                                onClick={() => setTab(t.id)}
                                className={`inline-flex items-center gap-2 px-4 py-3 text-[12px] tracking-[0.1em] uppercase border-b-2 transition-colors ${
                                    active
                                        ? "border-foreground text-foreground"
                                        : "border-transparent text-foreground/55 hover:text-foreground"
                                }`}
                            >
                                <Icon size={14} />
                                {t.label}
                                {typeof t.count === "number" && (
                                    <span className="text-foreground/40">({t.count})</span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </header>

            <main className="container-edge py-10">
                {loading ? (
                    <div className="py-20 flex items-center justify-center text-foreground/50">
                        <Loader2 className="animate-spin" />
                    </div>
                ) : tab === "projects" ? (
                    <ProjectsTab
                        projects={projects}
                        onCreate={() => setEditing({ ...emptyProject })}
                        onEdit={(p) => setEditing(p)}
                        onChanged={loadAll}
                    />
                ) : tab === "inquiries" ? (
                    <InquiriesTab
                        inquiries={inquiries}
                        onOpen={(i) => setActiveInq(i)}
                        onChanged={loadAll}
                    />
                ) : (
                    <AnalyticsTab analytics={analytics} />
                )}
            </main>

            {editing && (
                <ProjectEditor
                    project={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => {
                        setEditing(null);
                        loadAll();
                    }}
                />
            )}

            {activeInq && (
                <InquiryDetail
                    inq={activeInq}
                    onClose={() => setActiveInq(null)}
                />
            )}
        </div>
    );
}

function ProjectsTab({ projects, onCreate, onEdit, onChanged }) {
    const remove = async (id) => {
        if (!window.confirm("Delete this project?")) return;
        try {
            await api.delete(`/admin/portfolio/${id}`);
            toast.success("Project deleted");
            onChanged();
        } catch {
            toast.error("Could not delete");
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between">
                <h2 className="font-display text-3xl font-medium tracking-tight">
                    Portfolio projects
                </h2>
                <button
                    data-testid="admin-create-project"
                    onClick={onCreate}
                    className="btn-primary"
                >
                    <Plus size={14} /> New project
                </button>
            </div>

            {projects.length === 0 ? (
                <div className="mt-10 border border-border p-10 text-center text-foreground/55">
                    <p className="text-sm">No projects yet — create your first one.</p>
                </div>
            ) : (
                <div className="mt-8 border border-border">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[11px] tracking-[0.15em] uppercase text-foreground/55 bg-foreground/[0.03]">
                                <th className="p-4">Title</th>
                                <th className="p-4">Type</th>
                                <th className="p-4">Status</th>
                                <th className="p-4">Updated</th>
                                <th className="p-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {projects.map((p) => (
                                <tr
                                    key={p.id}
                                    data-testid={`admin-project-row-${p.slug}`}
                                    className="border-t border-border hover:bg-foreground/[0.02]"
                                >
                                    <td className="p-4">
                                        <div className="flex items-center gap-3">
                                            {p.cover_image && (
                                                <img
                                                    src={fileUrl(p.cover_image)}
                                                    alt=""
                                                    className="w-12 h-12 object-cover"
                                                />
                                            )}
                                            <div>
                                                <p className="font-medium">{p.title}</p>
                                                <p className="text-xs text-foreground/55">/{p.slug}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-4 text-foreground/70">{p.project_type}</td>
                                    <td className="p-4">
                                        <span className={`text-[10px] tracking-[0.15em] uppercase px-2 py-1 border ${p.published ? "border-foreground" : "border-foreground/30 text-foreground/55"}`}>
                                            {p.published ? "Published" : "Draft"}
                                        </span>
                                    </td>
                                    <td className="p-4 text-foreground/55 text-xs">
                                        {p.updated_at?.slice(0, 10)}
                                    </td>
                                    <td className="p-4 text-right">
                                        <button
                                            onClick={() => onEdit(p)}
                                            data-testid={`admin-edit-${p.slug}`}
                                            className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.1em] px-3 py-2 border border-border hover:bg-foreground hover:text-background transition-colors mr-2"
                                        >
                                            <Pencil size={12} /> Edit
                                        </button>
                                        <button
                                            onClick={() => remove(p.id)}
                                            data-testid={`admin-delete-${p.slug}`}
                                            className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.1em] px-3 py-2 border border-border hover:bg-destructive hover:text-destructive-foreground hover:border-destructive transition-colors"
                                        >
                                            <Trash2 size={12} /> Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function ProjectEditor({ project, onClose, onSaved }) {
    const isNew = !project.id;
    const [form, setForm] = useState({ ...project, services_used: project.services_used || [] });
    const [saving, setSaving] = useState(false);
    const [uploadingCover, setUploadingCover] = useState(false);
    const [uploadingGallery, setUploadingGallery] = useState(false);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const uploadFile = async (file, folder = "portfolio") => {
        const data = new FormData();
        data.append("file", file);
        data.append("folder", folder);
        const r = await api.post("/admin/upload", data, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        return r.data;
    };

    const onPickCover = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingCover(true);
        try {
            const r = await uploadFile(file, "portfolio/covers");
            update("cover_image", r.storage_path);
        } catch {
            toast.error("Upload failed");
        } finally {
            setUploadingCover(false);
            e.target.value = "";
        }
    };

    const onPickGallery = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploadingGallery(true);
        try {
            const next = [...form.gallery];
            for (const file of files) {
                const r = await uploadFile(file, "portfolio/gallery");
                next.push({
                    storage_path: r.storage_path,
                    original_filename: r.original_filename,
                    content_type: r.content_type,
                    file_kind: file.type.startsWith("image") ? "image" : "document",
                    caption: "",
                });
            }
            update("gallery", next);
        } catch {
            toast.error("One or more files failed to upload");
        } finally {
            setUploadingGallery(false);
            e.target.value = "";
        }
    };

    const removeGallery = (idx) => {
        update(
            "gallery",
            form.gallery.filter((_, i) => i !== idx)
        );
    };

    const save = async (e) => {
        e.preventDefault();
        if (!form.slug || !form.title || !form.project_type || !form.short_description) {
            toast.error("Slug, title, project type and short description are required");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                ...form,
                slug: form.slug.toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/-+/g, "-"),
                services_used: Array.isArray(form.services_used)
                    ? form.services_used
                    : String(form.services_used)
                          .split(",")
                          .map((s) => s.trim())
                          .filter(Boolean),
                sort_order: Number(form.sort_order) || 0,
            };
            if (isNew) {
                await api.post("/admin/portfolio", payload);
            } else {
                await api.put(`/admin/portfolio/${project.id}`, payload);
            }
            toast.success(isNew ? "Project created" : "Project updated");
            onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[80] bg-foreground/70 backdrop-blur-sm overflow-y-auto py-10 px-4 flex items-start justify-center">
            <form
                onSubmit={save}
                data-testid="admin-project-editor"
                className="relative bg-background w-full max-w-4xl border border-border"
            >
                <button
                    type="button"
                    onClick={onClose}
                    data-testid="admin-project-editor-close"
                    className="absolute top-3 right-3 p-2 hover:bg-foreground hover:text-background transition-colors"
                >
                    <X size={18} />
                </button>
                <div className="p-8">
                    <p className="overline">
                        {isNew ? "New project" : "Edit project"}
                    </p>
                    <h2 className="mt-3 font-display text-3xl font-medium tracking-tight">
                        {form.title || "Untitled"}
                    </h2>

                    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                        <FieldBlock label="Title *">
                            <input
                                required
                                className="field-line"
                                data-testid="proj-title"
                                value={form.title}
                                onChange={(e) => update("title", e.target.value)}
                            />
                        </FieldBlock>
                        <FieldBlock label="Slug *">
                            <input
                                required
                                className="field-line"
                                data-testid="proj-slug"
                                value={form.slug}
                                onChange={(e) => update("slug", e.target.value)}
                                placeholder="e.g. bespoke-bar"
                            />
                        </FieldBlock>
                        <FieldBlock label="Project type *">
                            <input
                                required
                                className="field-line"
                                data-testid="proj-type"
                                value={form.project_type}
                                onChange={(e) => update("project_type", e.target.value)}
                                placeholder="Scenic Design, Brand Activation…"
                            />
                        </FieldBlock>
                        <FieldBlock label="Year">
                            <input
                                className="field-line"
                                data-testid="proj-year"
                                value={form.year}
                                onChange={(e) => update("year", e.target.value)}
                            />
                        </FieldBlock>
                        <FieldBlock label="Client name">
                            <input
                                className="field-line"
                                value={form.client_name}
                                onChange={(e) => update("client_name", e.target.value)}
                            />
                        </FieldBlock>
                        <FieldBlock label="Location">
                            <input
                                className="field-line"
                                value={form.location}
                                onChange={(e) => update("location", e.target.value)}
                            />
                        </FieldBlock>
                        <FieldBlock label="Short description *" full>
                            <textarea
                                required
                                rows={2}
                                className="field-line resize-none"
                                data-testid="proj-short"
                                value={form.short_description}
                                onChange={(e) =>
                                    update("short_description", e.target.value)
                                }
                            />
                        </FieldBlock>
                        <FieldBlock label="Long description" full>
                            <textarea
                                rows={5}
                                className="field-line resize-none"
                                value={form.long_description}
                                onChange={(e) =>
                                    update("long_description", e.target.value)
                                }
                            />
                        </FieldBlock>
                        <FieldBlock label="Services used (comma-separated)" full>
                            <input
                                className="field-line"
                                value={
                                    Array.isArray(form.services_used)
                                        ? form.services_used.join(", ")
                                        : form.services_used
                                }
                                onChange={(e) =>
                                    update(
                                        "services_used",
                                        e.target.value
                                            .split(",")
                                            .map((s) => s.trim())
                                            .filter(Boolean)
                                    )
                                }
                            />
                        </FieldBlock>
                        <FieldBlock label="Sort order">
                            <input
                                type="number"
                                className="field-line"
                                value={form.sort_order}
                                onChange={(e) => update("sort_order", e.target.value)}
                            />
                        </FieldBlock>
                        <FieldBlock label="Status">
                            <select
                                className="field-line"
                                value={form.published ? "published" : "draft"}
                                onChange={(e) =>
                                    update("published", e.target.value === "published")
                                }
                            >
                                <option value="published">Published</option>
                                <option value="draft">Draft</option>
                            </select>
                        </FieldBlock>
                    </div>

                    {/* Cover */}
                    <div className="mt-10">
                        <p className="overline">Cover image</p>
                        <div className="mt-3 flex items-start gap-4">
                            {form.cover_image ? (
                                <img
                                    src={fileUrl(form.cover_image)}
                                    alt=""
                                    className="w-40 aspect-[4/3] object-cover border border-border"
                                />
                            ) : (
                                <div className="w-40 aspect-[4/3] border border-border flex items-center justify-center text-foreground/40 text-xs">
                                    No image
                                </div>
                            )}
                            <label className="inline-flex items-center gap-2 border border-border px-3 py-2 text-xs uppercase tracking-[0.1em] cursor-pointer hover:bg-foreground hover:text-background transition-colors">
                                {uploadingCover ? (
                                    <Loader2 size={14} className="animate-spin" />
                                ) : (
                                    <Upload size={14} />
                                )}
                                {uploadingCover ? "Uploading…" : "Upload cover"}
                                <input
                                    type="file"
                                    accept="image/*"
                                    hidden
                                    onChange={onPickCover}
                                    data-testid="proj-cover-input"
                                />
                            </label>
                        </div>
                    </div>

                    {/* Gallery */}
                    <div className="mt-10">
                        <div className="flex items-center justify-between">
                            <p className="overline">Gallery</p>
                            <label className="inline-flex items-center gap-2 border border-border px-3 py-2 text-xs uppercase tracking-[0.1em] cursor-pointer hover:bg-foreground hover:text-background transition-colors">
                                {uploadingGallery ? (
                                    <Loader2 size={14} className="animate-spin" />
                                ) : (
                                    <Upload size={14} />
                                )}
                                {uploadingGallery ? "Uploading…" : "Add files"}
                                <input
                                    type="file"
                                    multiple
                                    hidden
                                    onChange={onPickGallery}
                                    data-testid="proj-gallery-input"
                                />
                            </label>
                        </div>
                        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                            {form.gallery?.map((g, idx) => (
                                <div
                                    key={idx}
                                    className="relative border border-border group"
                                >
                                    {g.file_kind === "image" ? (
                                        <img
                                            src={fileUrl(g.storage_path)}
                                            alt=""
                                            className="w-full aspect-square object-cover"
                                        />
                                    ) : (
                                        <div className="w-full aspect-square flex flex-col items-center justify-center text-foreground/50 text-xs gap-2 p-3 text-center">
                                            <FileText size={20} />
                                            <span className="truncate w-full">
                                                {g.original_filename}
                                            </span>
                                        </div>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => removeGallery(idx)}
                                        className="absolute top-1 right-1 p-1 bg-background/90 hover:bg-foreground hover:text-background"
                                    >
                                        <X size={12} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="p-6 border-t border-border flex items-center justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn-secondary"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        data-testid="admin-project-save"
                        className="btn-primary"
                    >
                        {saving && <Loader2 size={14} className="animate-spin" />}
                        {saving ? "Saving…" : isNew ? "Create project" : "Save changes"}
                    </button>
                </div>
            </form>
        </div>
    );
}

function FieldBlock({ label, children, full }) {
    return (
        <div className={`${full ? "md:col-span-2" : ""} pt-5 pb-2`}>
            <label className="overline block mb-1">{label}</label>
            {children}
        </div>
    );
}

function InquiriesTab({ inquiries, onOpen, onChanged }) {
    const setStatus = async (id, status) => {
        try {
            const data = new FormData();
            data.append("status", status);
            await api.put(`/admin/inquiries/${id}/status`, data);
            onChanged();
        } catch {
            toast.error("Could not update status");
        }
    };
    const remove = async (id) => {
        if (!window.confirm("Delete this enquiry?")) return;
        try {
            await api.delete(`/admin/inquiries/${id}`);
            toast.success("Deleted");
            onChanged();
        } catch {
            toast.error("Could not delete");
        }
    };

    return (
        <div>
            <h2 className="font-display text-3xl font-medium tracking-tight">
                Enquiries
            </h2>

            {inquiries.length === 0 ? (
                <div className="mt-10 border border-border p-10 text-center text-foreground/55 text-sm">
                    No enquiries yet.
                </div>
            ) : (
                <div className="mt-8 border border-border">
                    {inquiries.map((i) => (
                        <div
                            key={i.id}
                            data-testid={`admin-inquiry-${i.id}`}
                            className="border-b border-border last:border-b-0 hover:bg-foreground/[0.02]"
                        >
                            <div className="p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
                                <div className="md:col-span-3">
                                    <p className="font-medium">{i.name}</p>
                                    <p className="text-xs text-foreground/55">{i.email}</p>
                                </div>
                                <div className="md:col-span-3 text-sm">
                                    <p className="text-foreground/80">
                                        {i.project_type || "—"}
                                    </p>
                                    <p className="text-xs text-foreground/55">
                                        {i.venue}
                                    </p>
                                </div>
                                <div className="md:col-span-2 text-xs text-foreground/55">
                                    {i.created_at?.slice(0, 10)}
                                </div>
                                <div className="md:col-span-2">
                                    <select
                                        value={i.status}
                                        onChange={(e) => setStatus(i.id, e.target.value)}
                                        className="field-line !py-2 !text-xs uppercase tracking-[0.1em]"
                                    >
                                        <option value="new">New</option>
                                        <option value="in_progress">In progress</option>
                                        <option value="quoted">Quoted</option>
                                        <option value="archived">Archived</option>
                                    </select>
                                </div>
                                <div className="md:col-span-2 flex justify-end gap-2">
                                    <button
                                        onClick={() => onOpen(i)}
                                        className="text-xs uppercase tracking-[0.1em] border border-border px-3 py-2 hover:bg-foreground hover:text-background"
                                    >
                                        View
                                    </button>
                                    <button
                                        onClick={() => remove(i.id)}
                                        className="text-xs uppercase tracking-[0.1em] border border-border px-3 py-2 hover:bg-destructive hover:text-destructive-foreground hover:border-destructive"
                                    >
                                        <Trash2 size={12} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function InquiryDetail({ inq, onClose }) {
    return (
        <div
            className="fixed inset-0 z-[80] bg-foreground/70 backdrop-blur-sm overflow-y-auto py-10 px-4 flex items-start justify-center"
            onClick={onClose}
        >
            <div
                className="bg-background w-full max-w-3xl border border-border relative"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    className="absolute top-3 right-3 p-2 hover:bg-foreground hover:text-background"
                >
                    <X size={18} />
                </button>
                <div className="p-8">
                    <p className="overline">Enquiry</p>
                    <h3 className="mt-3 font-display text-3xl font-medium tracking-tight">
                        {inq.name}
                    </h3>
                    <p className="text-foreground/60">
                        {inq.company || ""} {inq.company ? "·" : ""}{" "}
                        <a
                            href={`mailto:${inq.email}`}
                            className="hover:opacity-70"
                        >
                            {inq.email}
                        </a>{" "}
                        {inq.phone && ` · ${inq.phone}`}
                    </p>

                    <dl className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 text-sm border-y border-border py-6">
                        {[
                            ["Project type", inq.project_type],
                            ["Event date", inq.event_date],
                            ["Venue", inq.venue],
                            ["Budget", inq.budget_range],
                            ["Visualise", inq.visualise_what],
                            ["Submitted", inq.created_at?.replace("T", " ").slice(0, 16)],
                        ].map(([k, v]) => (
                            <div key={k}>
                                <dt className="overline">{k}</dt>
                                <dd className="mt-1 text-foreground/85">{v || "—"}</dd>
                            </div>
                        ))}
                    </dl>

                    <div className="mt-6">
                        <p className="overline">Message</p>
                        <p className="mt-2 whitespace-pre-wrap text-foreground/85 leading-relaxed">
                            {inq.message}
                        </p>
                    </div>

                    {inq.attachments?.length > 0 && (
                        <div className="mt-8">
                            <p className="overline">Attachments</p>
                            <ul className="mt-3 space-y-2">
                                {inq.attachments.map((a, idx) => (
                                    <li
                                        key={idx}
                                        className="flex items-center justify-between border border-border p-3 text-sm"
                                    >
                                        <div className="flex items-center gap-3 truncate">
                                            <FileText size={16} className="text-foreground/55" />
                                            <span className="truncate">
                                                {a.original_filename}
                                            </span>
                                        </div>
                                        <a
                                            href={fileUrl(a.storage_path)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs uppercase tracking-[0.1em] border border-border px-3 py-1.5 hover:bg-foreground hover:text-background"
                                        >
                                            Download →
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function AnalyticsTab({ analytics }) {
    if (!analytics) return null;
    const a = analytics;
    const max = Math.max(1, ...(a.daily_page_views || []).map((d) => d.count));
    return (
        <div>
            <h2 className="font-display text-3xl font-medium tracking-tight">
                Last {a.days} days
            </h2>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-px bg-border">
                {[
                    { label: "Page views", value: a.totals?.page_view || 0 },
                    { label: "Section views", value: a.totals?.section_view || 0 },
                    { label: "Total enquiries", value: a.total_inquiries || 0 },
                    { label: "New enquiries", value: a.new_inquiries || 0 },
                ].map((s) => (
                    <div key={s.label} className="bg-background p-6">
                        <p className="overline">{s.label}</p>
                        <p className="mt-3 font-display text-4xl font-medium tracking-tight">
                            {s.value}
                        </p>
                    </div>
                ))}
            </div>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-10">
                <div className="border border-border p-6">
                    <p className="overline">Daily page views</p>
                    <div className="mt-6 flex items-end gap-2 h-40">
                        {(a.daily_page_views || []).map((d) => (
                            <div
                                key={d.day}
                                className="flex-1 flex flex-col items-center gap-2 group"
                                title={`${d.day}: ${d.count}`}
                            >
                                <div
                                    className="w-full bg-foreground group-hover:bg-foreground/70 transition-colors"
                                    style={{
                                        height: `${(d.count / max) * 100}%`,
                                        minHeight: "2px",
                                    }}
                                />
                                <span className="text-[10px] text-foreground/55">
                                    {d.day.slice(5)}
                                </span>
                            </div>
                        ))}
                        {(a.daily_page_views || []).length === 0 && (
                            <p className="text-foreground/50 text-sm">No data yet.</p>
                        )}
                    </div>
                </div>

                <div className="border border-border p-6">
                    <p className="overline">Top sections</p>
                    <ul className="mt-4 space-y-3">
                        {(a.top_sections || []).map((s) => (
                            <li key={s.section} className="flex items-center justify-between text-sm">
                                <span className="text-foreground/80">{s.section}</span>
                                <span className="font-display tracking-tight">{s.count}</span>
                            </li>
                        ))}
                        {(a.top_sections || []).length === 0 && (
                            <p className="text-foreground/50 text-sm">No section views yet.</p>
                        )}
                    </ul>
                </div>
            </div>
        </div>
    );
}
